/**
 * UnsavedChangesDialog — themed "You have unsaved changes" prompt (built on Modal, not
 * window.confirm). Driven by `useUnsavedChangesGuard`.
 */

import { useRef } from 'react';
import { Modal } from './Modal';
import { Button } from './ui/button';

interface UnsavedChangesDialogProps {
  isOpen: boolean;
  /** Dialog body; defaults to generic wording. */
  message?: string;
  saving?: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
  onSave: () => void;
}

const DEFAULT_MESSAGE =
  'Save your changes before you leave? If you discard them, you keep what you last saved.';

export function UnsavedChangesDialog({
  isOpen,
  message = DEFAULT_MESSAGE,
  saving = false,
  onKeepEditing,
  onDiscard,
  onSave,
}: UnsavedChangesDialogProps) {
  const keepEditingRef = useRef<HTMLButtonElement>(null);
  return (
    <Modal
      isOpen={isOpen}
      onClose={onKeepEditing}
      title="You have unsaved changes"
      role="alertdialog"
      initialFocusRef={keepEditingRef}
      hideCloseButton
      data-testid="unsaved-changes-dialog"
    >
      <p className="text-sm text-muted-foreground">{message}</p>
      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button
          ref={keepEditingRef}
          type="button"
          variant="ghost"
          onClick={onKeepEditing}
          data-testid="unsaved-changes-keep-editing"
        >
          Keep editing
        </Button>
        <Button
          type="button"
          variant="outline"
          className="text-destructive"
          onClick={onDiscard}
          disabled={saving}
          data-testid="unsaved-changes-discard"
        >
          Discard changes
        </Button>
        <Button type="button" onClick={onSave} disabled={saving} data-testid="unsaved-changes-save">
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </Modal>
  );
}

export default UnsavedChangesDialog;
